import { useEffect } from 'react';
import { useGoogleLogin } from '@react-oauth/google';
import Cookies from 'js-cookie';
import { Link, useHistory } from 'react-router-dom';
import { AuthLayout } from 'modules/layouts/AuthLayout';
import * as yup from 'yup';
import { useQueryParams } from 'modules/routing/useQueryParams';
import UserService from 'api/services/httpServices/UserService';
import { useMutation } from '@tanstack/react-query';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import { Button } from 'modules/shared-components/button/button';
import { ApiErrorNotification } from 'modules/shared-components/notification/ApiErrorNotification';
import RecaptchaField from 'modules/authentication/components/RecaptchaField';
import Input from 'modules/shared-components/input/Input';
import { formatApiError } from 'helpers/api/apiErrors';
import { ReactComponent as GoogleLogo } from 'assets/img/icons/google-logo.svg';
import { LoginWithTelegram } from '../login/TelegramLoginButton';
import { NotificationDex } from '../dex/components/alerts/notification';

const getDefaultInitialState = (
  referrerId: string | undefined
): Partial<FormValues> => ({
  email: '',
  captcha: undefined,
  referrerId: referrerId || Cookies.get('referrerId') || '',
});

const schema = yup.object({
  email: yup
    .string()
    .typeError('Only strings are acceptable')
    .email('Incorrect email format')
    .required('This field is required'),
  captcha: yup.string().required('Incorrect captcha'),
  referrerId: yup.string(),
});

type FormValues = yup.InferType<typeof schema>;

type Props = {
  getInitialState?: typeof getDefaultInitialState;
};

const Signup = ({ getInitialState = getDefaultInitialState }: Props) => {
  const referrerFromUrl = useReferrerFromUrl();
  const history = useHistory();
  const {
    formState: { errors },
    control,
    register,
    handleSubmit,
    getValues,
  } = useForm<FormValues>({
    resolver: yupResolver(schema),
    defaultValues: getInitialState(referrerFromUrl),
  });

  const {
    mutate: signUpWithEmail,
    error: signUpWithEmailError,
    isLoading: emailSignUpLoading,
  } = useMutation({
    mutationFn: UserService.signUpWithEmail,
    onSuccess: () => {
      history.push('/thank-you-for-signing-up', {
        signupKind: 'email',
      });
    },
  });

  const {
    mutate: signUpWithGoogle,
    error: signUpWithGoogleError,
    isLoading: googleSignUpLoading,
  } = useMutation({
    mutationFn: UserService.signUpWithGoogle,
    onSuccess: () => {
      history.push('/thank-you-for-signing-up', {
        signupKind: 'google',
      });
    },
  });

  const onSubmit = handleSubmit((values: FormValues) => {
    signUpWithEmail({
      email_address: values.email,
      recaptcha_token: values.captcha,
      referral_code: values.referrerId,
    });
  });

  const sendSignupWithGoogle = useGoogleLogin({
    onSuccess: (codeResponse) => {
      signUpWithGoogle({
        code: codeResponse.code,
        referral_code: getValues('referrerId'),
      });
    },
    flow: 'auth-code',
  });

  const clickedSignupWithGoogle = (event: any) => {
    event.preventDefault();
    sendSignupWithGoogle();
  };

  return (
    <div className="wrapper">
      <AuthLayout>
        <div className="lg:grid lg:grid-cols-2 ">
          <div className="px-4 lg:px-10">
            <h1 className="text-2xl lg:text-5xl xxs:text-center lg:text-left">
              Manage and automate your crypto investments with Mizar
            </h1>
            <div className="pt-16 hidden lg:block">
              <h5 className="text-2xl xxs:mb-1">Crypto Copy-Trading</h5>
              <p className="text-lg m-0">
                Copy successful traders and watch your portfolio grow.
              </p>
              <h5 className="text-2xl  xxs:mb-1 pt-8">Bots & Trading Tools</h5>
              <p className="text-lg m-0">
                Create trading bots and use smart trading tools.
              </p>
              <h5 className="text-2xl  xxs:mb-1 pt-8">No fixed fees</h5>
              <p className="text-lg m-0">
                No subscription fee, monthly fee or credit card required.
              </p>
            </div>
          </div>

          <div className="xxs:px-4 lg:px-24">
            <div className="py-10 px-6 bg-black-700 shadow-lg rounded-lg">
              <div data-testid="sign_up_email_form">
                <p className="text-lg ">
                  Sign-up and trade your first $10,000 for free.
                </p>

                <form
                  data-testid="SignUp-form-email"
                  onSubmit={(e) => {
                    void onSubmit(e);
                  }}
                >
                  <div className="xxs:flex flex-col xxs:mt-3 xxs:mb-4">
                    <Input
                      {...register('email')}
                      label="Email Address"
                      placeholder="Your email address"
                      errorText={errors.email?.message}
                      data-testid="SignUp-form-email-input"
                    />
                  </div>

                  <div className="xxs:flex flex-col xxs:mt-3 xxs:mb-4">
                    <Input
                      {...register('referrerId')}
                      label="Referral Code (optional)"
                      placeholder="Your referral code"
                      errorText={errors.referrerId?.message}
                      data-testid="referral_code_input_form"
                    />
                  </div>

                  <RecaptchaField control={control} />

                  <ApiErrorNotification
                    error={signUpWithGoogleError}
                    className="xxs:my-2"
                  />

                  {signUpWithEmailError && (
                    <NotificationDex type="error" className="xxs:my-2">
                      {`${formatApiError(
                        signUpWithEmailError,
                        'Could not sign up.'
                      )}. Maybe you already have an account?`}
                    </NotificationDex>
                  )}

                  <Button
                    variant="primary"
                    type="submit"
                    className="w-full xxs:mb-2"
                    size="large"
                    loading={emailSignUpLoading}
                  >
                    SIGN UP
                  </Button>

                  <div>
                    <div data-testid="signup-with-google-div">
                      <Button
                        onClick={clickedSignupWithGoogle}
                        loading={googleSignUpLoading}
                        size="large"
                        type="button"
                        className="w-full xxs:bg-white hover:bg-white-600 text-black-700 xxs:py-1.5"
                      >
                        <GoogleLogo title="google logo" height="32" />
                        <span className="xxs:pl-2">SIGN UP WITH GOOGLE</span>
                      </Button>
                    </div>
                  </div>
                  <div className="xxs:mt-2">
                    <LoginWithTelegram>Sign UP WITH TELEGRAM</LoginWithTelegram>
                  </div>

                  <p className="font-sm pt-2 text-center">
                    Already signed up?{' '}
                    <Link
                      to="/login"
                      className="text-blue-500 hover:text-blue-600"
                    >
                      Login instead
                    </Link>
                  </p>
                </form>
              </div>
            </div>
          </div>
        </div>
      </AuthLayout>
    </div>
  );
};

function useReferrerFromUrl() {
  const [{ referrer_id: referrerFromUrl }] = useQueryParams({
    referrer_id: yup.string(),
  });
  useEffect(() => {
    if (referrerFromUrl) {
      Cookies.set('referrerId', referrerFromUrl, { expires: 30 });
    }
  }, [referrerFromUrl]);

  return referrerFromUrl;
}

export default Signup;
